import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  forwardRef,
  inject,
  input,
  output,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { NeoSlideToggle, NeoSlideToggleModule } from '@neogov/pattern-library/neo-slide-toggle';
import { NEO_TOOLTIP_SCROLL_STRATEGY_FACTORY_PROVIDER, NeoTooltipV2 } from '@neogov/pattern-library/neo-tooltip-v2';
import { SpinnerComponent } from '../spinner';

export type SwitchColor = 'primary' | 'accent' | 'warn';
export type SwitchLabelPosition = 'before' | 'after';

@Component({
  selector: 'cm-switch',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    NeoSlideToggleModule,
    SpinnerComponent
  ],
  templateUrl: './switch.component.html',
  styleUrl: './switch.component.less',
  // Host the tooltip on the component element (never disabled), so `[tooltip]`
  // still fires when the toggle itself is disabled. The LESS clears pointer-events
  // on the disabled toggle so hover reaches the host. See cm-button for the same pattern.
  hostDirectives: [{ directive: NeoTooltipV2, inputs: ['neoTooltip: tooltip'] }],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SwitchComponent),
      multi: true,
    },
    NEO_TOOLTIP_SCROLL_STRATEGY_FACTORY_PROVIDER,
  ],
})
export class SwitchComponent implements ControlValueAccessor {
  private readonly _cdr = inject(ChangeDetectorRef);

  @ViewChild(NeoSlideToggle) private _slideToggle!: NeoSlideToggle;

  public readonly checked = input<boolean>(false);
  public readonly disabled = input<boolean>(false);
  public readonly color = input<SwitchColor>('primary');
  public readonly labelPosition = input<SwitchLabelPosition>('after');
  public readonly ariaLabel = input<string>('');
  public readonly ariaLabelledby = input<string>('');
  public readonly ariaDescribedby = input<string>('');
  public readonly id = input<string>('');
  public readonly name = input<string>('');
  public readonly required = input<boolean>(false);
  public readonly disableRipple = input<boolean>(false);
  public readonly autId = input<string>('');
  public readonly spaceBetweenLabelAndToggle = input<boolean>(false);
  public readonly alignStart = input<boolean>(false);
  public readonly labelBold = input<boolean>(false);
  public readonly loading = input<boolean>(false);
  public readonly confirmBeforeChange = input<boolean>(false);

  public readonly checkedChange = output<boolean>();

  // Internal state for form control
  public value: boolean | null = null;
  public isDisabled = false;
  private _isFormControl = false;

  // ControlValueAccessor callbacks
  private _onChange: (value: boolean) => void = () => { /* Initialized by Angular forms */ };
  private _onTouched: () => void = () => { /* Initialized by Angular forms */ };

  /**
   * Gets the effective checked state.
   * Prioritizes form control value over input binding.
   */
  public get effectiveChecked(): boolean {
    return this.value !== null ? this.value : this.checked();
  }

  /**
   * Gets the effective disabled state.
   * Combines form control disabled state with input binding.
   */
  public get effectiveDisabled(): boolean {
    return this.isDisabled || this.disabled();
  }

  public onToggleChange(checked: boolean): void {
    if (this._isFormControl) {
      this.value = checked;
    } else if (this.confirmBeforeChange()) {
      this._slideToggle.checked = !checked;
    }
    this._onChange(checked);
    this._onTouched();
    this.checkedChange.emit(checked);
  }

  // ControlValueAccessor implementation
  public writeValue(value: boolean): void {
    this._isFormControl = true;
    this.value = value ?? false;
    this._cdr.markForCheck();
  }

  public registerOnChange(fn: (value: boolean) => void): void {
    this._onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    this._cdr.markForCheck();
  }
}
